import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Copy,
  Mail,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  SearchX,
  Trash2,
} from "lucide-react";
import {
  deleteCampaign,
  duplicateCampaign,
  errorMessage,
  updateCampaign,
  type Campaign,
  type CampaignStatus,
  type EmailProjectAccess,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { CampaignStatusBadge, Hint, ProviderModeBadge, ScopeBadge } from "@/components/email/EmailBadges";
import {
  CONTACT_STATUS,
  CONTACT_STATUS_ORDER,
  PREVIOUS_TEAM_NOTE,
  canChangeCampaignState,
  canDeleteCampaign,
  campaignReplies,
  deleteCampaignText,
  leadsInCampaign,
} from "@/lib/email";
import { campaignPath } from "@/lib/emailRoutes";
import { formatDateTime, formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";

/**
 * The campaigns, one dense row each: status, name, leads, replies, sender,
 * provider. Each row opens its workspace in the campaign's own project; its
 * actions follow the role in that project.
 */
export function CampaignList({
  campaigns,
  error,
  filtered,
  showProject,
  accessFor,
  external,
  canCreate,
  onCreate,
  onChanged,
}: {
  campaigns: Campaign[] | null;
  error: string | null;
  /** A search or filter hides campaigns: the empty state says so. */
  filtered: boolean;
  /** Several projects are listed together ("All"). */
  showProject: boolean;
  accessFor: (campaign: Campaign) => EmailProjectAccess | null;
  external: boolean;
  canCreate: boolean;
  onCreate: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const open = (campaign: Campaign) => navigate(campaignPath(campaign.project_slug, campaign.id));

  async function setStatus(campaign: Campaign, status: CampaignStatus) {
    if (status === "ARCHIVED") {
      const ok = await confirm({
        title: `Archive “${campaign.name}”?`,
        description: `${
          external && campaign.provider_mode === "LIVE"
            ? "Lemlist stops sending for this campaign."
            : external
              ? "Nothing changes in Lemlist: this campaign was never synced."
              : "Dry run: nothing reaches Lemlist."
        } Contacts keep their history; the campaign leaves your active lists and can be restored later.`,
        confirmLabel: "Archive",
        variant: "warning",
      });
      if (!ok) return;
    }
    try {
      await updateCampaign(campaign.project_slug, campaign.id, { status });
      toast.success(
        status === "ARCHIVED" ? "Campaign archived" : status === "PAUSED" ? "Campaign paused" : "Campaign active",
      );
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function duplicate(campaign: Campaign) {
    try {
      const copy = await duplicateCampaign(campaign.project_slug, campaign.id);
      toast.success(`“${copy.name}” created as a draft`);
      navigate(campaignPath(copy.project_slug, copy.id));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function remove(campaign: Campaign) {
    const ok = await confirm({
      title: `Delete “${campaign.name}” for good?`,
      description: deleteCampaignText(campaign),
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const result = await deleteCampaign(campaign.project_slug, campaign.id);
      toast.success(result.note ?? `“${result.name}” deleted`);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-plane bg-white p-6 text-sm text-rose-600 shadow-plane">
        <AlertTriangle className="h-4 w-4" /> {error}
      </div>
    );
  }

  if (campaigns === null) {
    return (
      <div className="space-y-2 rounded-plane bg-white p-4 shadow-plane" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-11 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (!campaigns.length) {
    return filtered ? (
      <div className="flex flex-col items-center rounded-plane bg-white px-6 py-14 text-center shadow-plane">
        <SearchX className="h-8 w-8 text-slate-300" />
        <p className="mt-3 text-sm font-medium text-slate-700">No campaign matches</p>
        <p className="mt-1 text-xs text-slate-500">Clear the search or pick another status.</p>
      </div>
    ) : (
      <div className="flex flex-col items-center rounded-plane bg-white px-6 py-16 text-center shadow-plane">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500">
          <Mail className="h-5 w-5" />
        </span>
        <h2 className="mt-4 text-base font-semibold text-slate-900">No campaign yet</h2>
        <p className="mt-1 max-w-md text-sm text-slate-500">
          A campaign is a sequence of emails — a first message and its follow-ups — written once with variables
          and AI passages, rendered for each lead, then launched to Lemlist.
        </p>
        {canCreate && (
          <Button variant="primary" size="sm" className="mt-5" onClick={onCreate}>
            <Plus className="h-3.5 w-3.5" />
            Create campaign
          </Button>
        )}
      </div>
    );
  }

  const headers = ["Campaign", "Status", "Leads", "Replies", "Provider", ""];

  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              {headers.map((label, index) => (
                <th
                  key={label || index}
                  className="whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400 first:pl-4"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => {
              const access = accessFor(campaign);
              const canManage = !!access?.can_manage;
              const canChangeState = canChangeCampaignState(access, campaign);
              const replies = campaignReplies(campaign);
              return (
                <tr
                  key={`${campaign.project_id}-${campaign.id}`}
                  onClick={() => open(campaign)}
                  className={cn(
                    "group cursor-pointer border-b border-slate-50 transition-colors last:border-0 hover:bg-indigo-50/30",
                    campaign.status === "ARCHIVED" && "opacity-60",
                  )}
                >
                  <td className="max-w-[360px] py-2.5 pl-4 pr-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <Link
                        to={campaignPath(campaign.project_slug, campaign.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="truncate font-medium text-slate-900 hover:text-indigo-700"
                      >
                        {campaign.name}
                      </Link>
                      {campaign.execution_lock && (
                        <Hint content={campaign.execution_lock.reason}>
                          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                            Locked pilot
                          </span>
                        </Hint>
                      )}
                      {campaign.provider_legacy && (
                        <Hint content={PREVIOUS_TEAM_NOTE}>
                          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            Previous team
                          </span>
                        </Hint>
                      )}
                      {campaign.unsynced_changes && campaign.provider_mode !== "NONE" && !campaign.provider_legacy && (
                        <Hint content="Changes not sent to Lemlist yet — sync the campaign.">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                        </Hint>
                      )}
                    </div>
                    <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-slate-400">
                      {showProject && (
                        <span className="inline-flex shrink-0 items-center gap-1 text-slate-500">
                          <ScopeBadge scope={campaign.scope} className="px-1.5 py-0 text-[10px]" />
                          {campaign.project_name}
                          <span className="text-slate-300">·</span>
                        </span>
                      )}
                      <span className="shrink-0">
                        {campaign.steps_count} {campaign.steps_count === 1 ? "step" : "steps"}
                      </span>
                      <span className="text-slate-300">·</span>
                      {campaign.execution_lock ? (
                        <span className="truncate">No sender · locked pilot</span>
                      ) : campaign.settings.sender?.email ? (
                        <span className="truncate" title={`Sent from ${campaign.settings.sender.email}`}>
                          {campaign.settings.sender.email}
                        </span>
                      ) : (
                        <Hint content="No mailbox chosen: Lemlist's default sender for the team.">
                          <span className="truncate">Default sender</span>
                        </Hint>
                      )}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    <CampaignStatusBadge status={campaign.status} />
                  </td>
                  <td className="px-3 py-2.5">
                    <LeadsMix counts={campaign.contacts_by_status} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    {replies.count === null ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <Hint content={replies.hint}>
                        <span className="tabular-nums">
                          <span className="font-semibold text-slate-800">{formatNumber(replies.count)}</span>
                          {replies.rate !== null && (
                            <span className="ml-1.5 text-xs text-slate-400">{replies.rate}</span>
                          )}
                        </span>
                      </Hint>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <div className="flex items-center gap-2" title={campaign.last_synced_at ? `Synced ${formatDateTime(campaign.last_synced_at)}` : "Never synced"}>
                      <ProviderModeBadge mode={campaign.provider_mode} />
                      {campaign.sync_error ? (
                        <Hint content={campaign.sync_error}>
                          <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />
                        </Hint>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 opacity-70 group-hover:opacity-100"
                            aria-label={`Actions for ${campaign.name}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => open(campaign)}>
                            <Mail className="h-4 w-4" /> Open
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => void duplicate(campaign)}>
                            <Copy className="h-4 w-4" /> Duplicate
                          </DropdownMenuItem>
                          {canChangeState && <DropdownMenuSeparator />}
                          {canChangeState && campaign.status === "ACTIVE" && (
                            <DropdownMenuItem onClick={() => void setStatus(campaign, "PAUSED")}>
                              <Pause className="h-4 w-4" /> Pause
                            </DropdownMenuItem>
                          )}
                          {canChangeState && campaign.status === "PAUSED" && (
                            <DropdownMenuItem onClick={() => void setStatus(campaign, "ACTIVE")}>
                              <Play className="h-4 w-4" /> Resume
                            </DropdownMenuItem>
                          )}
                          {!canChangeState ? null : campaign.status === "ARCHIVED" ? (
                            <DropdownMenuItem onClick={() => void setStatus(campaign, "PAUSED")}>
                              <ArchiveRestore className="h-4 w-4" /> Restore (paused)
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              className="text-rose-600 focus:text-rose-700"
                              onClick={() => void setStatus(campaign, "ARCHIVED")}
                            >
                              <Archive className="h-4 w-4" /> Archive
                            </DropdownMenuItem>
                          )}
                          {canDeleteCampaign(access, campaign) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-rose-600 focus:text-rose-700"
                                onClick={() => void remove(campaign)}
                              >
                                <Trash2 className="h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
        {formatNumber(campaigns.length)} {campaigns.length === 1 ? "campaign" : "campaigns"}
      </p>
    </div>
  );
}

/** The campaign's leads as a thin stacked bar by status, with the total. */
function LeadsMix({ counts }: { counts: Record<string, number> }) {
  const total = leadsInCampaign(counts);
  if (!total) return <span className="text-xs text-slate-400">No lead</span>;
  const parts = CONTACT_STATUS_ORDER.filter((s) => s !== "EMAIL_REMOVED" && counts[s]).map((s) => ({
    status: s,
    count: counts[s],
  }));
  return (
    <Hint
      content={
        <span className="block space-y-0.5">
          {parts.map((p) => (
            <span key={p.status} className="flex items-center justify-between gap-4">
              <span>{CONTACT_STATUS[p.status].label}</span>
              <span className="tabular-nums">{p.count}</span>
            </span>
          ))}
        </span>
      }
    >
      <div className="w-24">
        <span className="text-xs font-semibold tabular-nums text-slate-800">{formatNumber(total)}</span>
        <div className="mt-1 flex h-1.5 overflow-hidden rounded-full bg-slate-100">
          {parts.map((p) => (
            <span key={p.status} className={CONTACT_STATUS[p.status].dot} style={{ width: `${(p.count / total) * 100}%` }} />
          ))}
        </div>
      </div>
    </Hint>
  );
}
